import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getQuiz, createQuiz, updateQuiz, getTeacherClasses, generateQuestions, estimateTime, uploadDocument, deleteDocument, listDocuments, getTagsTree } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { QcmPreviewDialog } from '../components/QcmPreviewDialog';
import { DraftBanner } from '../components/quiz/DraftBanner';
import { UnsavedChangesDialog, LeavePageDialog, NoStudentsDialog } from '../components/quiz/QuizEditorDialogs';
import { toast } from 'sonner';
import { Save, Plus, Clock, Eye } from 'lucide-react';
import { QuizAIPanel, DIFFICULTES } from '../components/quiz/QuizAIPanel';
import { QuestionCard } from '../components/quiz/QuestionCard';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

const snapshotOf = (titre, classe, bareme, questions) => JSON.stringify({ titre, classe, bareme, questions: questions.map(({ _id, ...rest }) => rest) });

const readDraft = (key) => { try { return JSON.parse(localStorage.getItem(key)); } catch { return null; } };

const emptyQuestion = () => ({ texte: '', options: ['', '', '', ''], bonne_reponse: 'A', points: 1, type: 'single', notion_id: '', _id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}` });

/** Flatten a tag tree into a flat list of notions with their full path label (for auto-suggest matching). */
function flattenTagTree(tree, path = []) {
  const result = [];
  for (const node of tree || []) {
    const currentPath = [...path, node.label];
    if (node.type === 'notion') {
      result.push({ tag_id: node.tag_id, label: node.label, path: currentPath.slice(0, -1).join(' › '), fullLabel: currentPath.join(' › ') });
    }
    if (node.children?.length) {
      result.push(...flattenTagTree(node.children, currentPath));
    }
  }
  return result;
}

import { usePageTitle } from '../hooks/usePageTitle';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

export default function QuizEditor() {
  usePageTitle('Éditeur QCM');
  const { quizId } = useParams();
  const navigate = useNavigate();
  const isEdit = !!quizId;

  const [titre, setTitre] = useState('');
  const [classe, setClasse] = useState('');
  const [bareme, setBareme] = useState(20);
  const [questions, setQuestions] = useState([emptyQuestion()]);
  const [saving, setSaving] = useState(false);
  const [teacherClasses, setTeacherClasses] = useState([]);
  const [aiSubject, setAiSubject] = useState('');
  const [aiCount, setAiCount] = useState(5);
  const [aiLoading, setAiLoading] = useState(false);
  const [difficulte, setDifficulte] = useState('moyen');
  const [estimatedTime, setEstimatedTime] = useState(null);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [pdfCount, setPdfCount] = useState(0);
  const [noStudentsWarning, setNoStudentsWarning] = useState(false);
  const [notionTags, setNotionTags] = useState([]);
  const [tagsTree, setTagsTree] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [unsavedPrompt, setUnsavedPrompt] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const draftKey = `qcm_draft_${quizId || 'new'}`;
  const [sciMode, setSciMode] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setQuestions(prev => {
      const oldIndex = prev.findIndex(q => q._id === active.id);
      const newIndex = prev.findIndex(q => q._id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [classesRes, docsRes, tagsRes] = await Promise.all([getTeacherClasses(), listDocuments(), getTagsTree().catch(() => ({ data: [] }))]);
        setTeacherClasses(classesRes.data);
        setUploadedDocs(docsRes.data);
        setNotionTags(flattenTagTree(tagsRes.data));
        setTagsTree(tagsRes.data || []);
        if (isEdit) {
          const res = await getQuiz(quizId);
          const loadedQs = res.data.questions.map((q, i) => ({ texte: q.texte, options: q.options, bonne_reponse: q.bonne_reponse, points: q.points || 1, type: q.type || 'single', notion_id: q.notion_id || '', _id: q._id || `q_loaded_${i}_${Math.random().toString(36).slice(2, 7)}` }));
          setTitre(res.data.titre); setClasse(res.data.classe); setBareme(res.data.bareme);
          setQuestions(loadedQs);
          const saved = snapshotOf(res.data.titre, res.data.classe, res.data.bareme, loadedQs);
          setSavedSnapshot(saved);
          const draft = readDraft(`qcm_draft_${quizId}`);
          if (draft && snapshotOf(draft.titre, draft.classe, draft.bareme, draft.questions) !== saved) setPendingDraft(draft);
          else if (draft) localStorage.removeItem(`qcm_draft_${quizId}`);
          const exams = (await import('../lib/api')).getExams;
          try { const ex = await exams?.(quizId); setPdfCount(ex?.data?.length || 0); } catch (e) { console.warn('PDF count unavailable:', e?.message); }
        } else {
          const draft = readDraft('qcm_draft_new');
          if (draft) setPendingDraft(draft);
        }
      } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur de chargement du QCM'); }
      setLoaded(true);
    };
    load();
  }, [isEdit, quizId]);

  const updateTimeEstimate = useCallback(async () => {
    const qs = questions.filter(q => q.texte);
    if (qs.length === 0) { setEstimatedTime(null); return; }
    try { setEstimatedTime((await estimateTime({ num_questions: qs.length, difficulte, classe })).data); }
    catch (e) { console.warn('Time estimate unavailable:', e?.message); }
  }, [questions, difficulte, classe]);

  useEffect(() => { updateTimeEstimate(); }, [updateTimeEstimate]);

  // Question mutation helpers
  const updateQuestion = (idx, field, val) => setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, [field]: val } : q));
  const updateOption = (qIdx, oIdx, val) => setQuestions(prev => prev.map((q, i) => i !== qIdx ? q : { ...q, options: q.options.map((o, j) => j === oIdx ? val : o) }));
  const addOption = (qIdx) => setQuestions(prev => prev.map((q, i) => (i !== qIdx || q.options.length >= 6) ? q : { ...q, options: [...q.options, ''] }));
  const removeOption = (qIdx, oIdx) => setQuestions(prev => prev.map((q, i) => (i !== qIdx || q.options.length <= 2) ? q : { ...q, options: q.options.filter((_, j) => j !== oIdx) }));
  const removeQuestion = (idx) => { if (questions.length > 1) setQuestions(prev => prev.filter((_, i) => i !== idx)); };

  const toggleCorrectAnswer = (qIdx, letter) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== qIdx) return q;
      if (q.type === 'multiple') {
        const current = q.bonne_reponse ? q.bonne_reponse.split(',').filter(Boolean) : [];
        const updated = current.includes(letter) ? current.filter(l => l !== letter) : [...current, letter].sort();
        return { ...q, bonne_reponse: updated.length > 0 ? updated.join(',') : letter };
      }
      return { ...q, bonne_reponse: letter };
    }));
  };

  const toggleQuestionType = (qIdx) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== qIdx) return q;
      return q.type === 'single' ? { ...q, type: 'multiple' } : { ...q, type: 'single', bonne_reponse: q.bonne_reponse.split(',')[0] || 'A' };
    }));
  };

  const isDirty = isEdit && savedSnapshot !== null && savedSnapshot !== snapshotOf(titre, classe, bareme, questions);
  const { pendingHref, confirmLeave, cancelLeave } = useUnsavedGuard(isDirty);

  const hasContent = !!titre || questions.some(q => q.texte || q.options.some(o => o));
  const shouldDraft = loaded && !pendingDraft && (isEdit ? isDirty : hasContent);
  useEffect(() => {
    if (!shouldDraft) return;
    const t = setTimeout(() => {
      const now = Date.now();
      localStorage.setItem(draftKey, JSON.stringify({ titre, classe, bareme, questions, savedAt: now }));
      setDraftSavedAt(now);
    }, 2000);
    return () => clearTimeout(t);
  }, [titre, classe, bareme, questions, shouldDraft, draftKey]);

  const restoreDraft = () => {
    setTitre(pendingDraft.titre || ''); setClasse(pendingDraft.classe || ''); setBareme(pendingDraft.bareme || 20);
    setQuestions(pendingDraft.questions?.length ? pendingDraft.questions : [emptyQuestion()]);
    setDraftSavedAt(pendingDraft.savedAt);
    setPendingDraft(null);
    toast.success('Brouillon restauré');
  };
  const discardDraft = () => { localStorage.removeItem(draftKey); setPendingDraft(null); };

  const handleSave = async () => {
    if (!titre || !classe) { toast.error('Titre et classe obligatoires'); return false; }
    if (!questions.every(q => q.texte && q.options.every(o => o) && q.bonne_reponse)) { toast.error('Complétez toutes les questions et options'); return false; }
    setSaving(true);
    let ok = false;
    try {
      const data = { titre, classe, bareme, questions: questions.map(({ _id, ...rest }) => ({ ...rest, notion_id: rest.notion_id || null })) };
      if (isEdit) { await updateQuiz(quizId, data); toast.success('QCM mis à jour'); setSavedSnapshot(snapshotOf(titre, classe, bareme, questions)); }
      else { const res = await createQuiz(data); toast.success('QCM créé'); localStorage.removeItem(draftKey); navigate(`/quizzes/${res.data.quiz_id}`); }
      localStorage.removeItem(draftKey);
      setDraftSavedAt(null);
      ok = true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
      if (e.response?.status === 403) setTimeout(() => toast('Passez à Pro pour créer des QCM illimités', { action: { label: 'Passer à Pro', onClick: () => navigate('/abonnement') } }), 1000);
    }
    setSaving(false);
    return ok;
  };

  const onDropDocs = async (files) => {
    setUploading(true);
    for (const file of files) {
      try {
        const res = await uploadDocument(file);
        setUploadedDocs(prev => [res.data, ...prev]);
        toast.success(`${file.name} chargé`);
      } catch (e) { toast.error(e.response?.data?.detail || `Erreur: ${file.name}`); }
    }
    setUploading(false);
  };

  const handleDeleteDoc = async (docId) => {
    try { await deleteDocument(docId); setUploadedDocs(prev => prev.filter(d => d.doc_id !== docId)); toast.success('Document supprimé'); }
    catch { toast.error('Erreur suppression'); }
  };

  const handleAIGenerate = async () => {
    if (!aiSubject && uploadedDocs.length === 0) { toast.error('Indiquez un sujet ou chargez un document'); return; }
    setAiLoading(true);
    try {
      const res = await generateQuestions({
        sujet: aiSubject || '', nombre: aiCount, classe: classe || '', difficulte,
        document_ids: uploadedDocs.length > 0 ? uploadedDocs.map(d => d.doc_id) : null
      });
      if (res.data.questions) {
        const newQs = res.data.questions.map((q, i) => ({
          texte: q.texte,
          texte_ast: q.texte_ast || null,
          options: q.options,
          options_ast: q.options_ast || null,
          bonne_reponse: q.bonne_reponse,
          points: q.points || 1,
          type: q.type || 'single',
          schema_version: q.schema_version || null,
          notion_id: '',
          _id: `q_ai_${Date.now()}_${i}`,
        }));
        setQuestions(prev => [...prev.filter(q => q.texte), ...newQs]);
        toast.success(`${newQs.length} questions générées par IA`);
        // Auto-enable science mode if formulas detected
        const hasFormulas = newQs.some(q => q.texte?.includes('$') || q.options?.some(o => o?.includes('$')));
        if (hasFormulas && !sciMode) setSciMode(true);
      } else { toast.error(res.data.error || 'Erreur de génération'); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur de génération IA'); }
    setAiLoading(false);
  };

  const handleDownloadQCMs = () => {
    if (!quizId) { toast.error('Sauvegardez le QCM avant de télécharger'); return; }
    if (isDirty) { setUnsavedPrompt(true); return; }
    setPreviewOpen(true);
  };

  const handleSaveThenLeave = async () => {
    if (await handleSave()) confirmLeave();
  };

  const handleSaveThenPreview = async () => {
    const ok = await handleSave();
    setUnsavedPrompt(false);
    if (ok) setPreviewOpen(true);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="quiz-editor-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight">{isEdit ? 'Modifier le QCM' : 'Nouveau QCM'}</h1>
          {draftSavedAt && (isEdit ? isDirty : hasContent) && (
            <p className="text-xs text-amber-400/80 mt-1" data-testid="draft-status">Brouillon enregistré localement à {new Date(draftSavedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</p>
          )}
          {estimatedTime && (
            <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground" data-testid="time-estimate">
              <Clock className="w-4 h-4 text-primary" />
              Temps estimé : <span className="text-foreground font-bold">{estimatedTime.formatted}</span>
              <span className="text-xs">({questions.filter(q => q.texte).length} questions, {DIFFICULTES.find(d => d.value === difficulte)?.label})</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="save-quiz-btn" onClick={handleSave} disabled={saving} size="sm" className="h-9 bg-primary text-primary-foreground hover:bg-blue-600 relative">
            <Save className="w-4 h-4" />
            <span className="hidden sm:inline ml-1.5">{saving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
            {isDirty && <span data-testid="unsaved-indicator" title="Modifications non sauvegardées" className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-400 ring-2 ring-background animate-pulse" />}
          </Button>
          {isEdit && (
            <Button data-testid="download-qcms-btn" onClick={handleDownloadQCMs} size="sm" className="h-9 bg-emerald-600 text-white hover:bg-emerald-700">
              <Eye className="w-4 h-4" />
              <span className="hidden sm:inline ml-1.5">Aperçu & Télécharger</span>
            </Button>
          )}
        </div>
      </div>

      {pendingDraft && <DraftBanner draft={pendingDraft} onRestore={restoreDraft} onDiscard={discardDraft} />}


      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              <Label>Titre du QCM</Label>
              <Input data-testid="quiz-titre" value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="ex: Contrôle Maths Section 5" className="text-base" />
            </div>
            <div>
              <Label>Classe</Label>
              <Select value={classe} onValueChange={setClasse}>
                <SelectTrigger data-testid="quiz-classe"><SelectValue placeholder="Sélectionnez une classe" /></SelectTrigger>
                <SelectContent>
                  {teacherClasses.map(tc => <SelectItem key={tc.class_id} value={tc.nom}>{tc.nom}</SelectItem>)}
                  {teacherClasses.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Créez d'abord une classe depuis la page Élèves</div>}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Barème</Label>
              <Input data-testid="quiz-bareme" type="number" value={bareme} onChange={(e) => setBareme(Number(e.target.value))} min={1} max={100} />
            </div>
          </div>
        </CardContent>
      </Card>

      <QuizAIPanel aiSubject={aiSubject} setAiSubject={setAiSubject} aiCount={aiCount} setAiCount={setAiCount} aiLoading={aiLoading} difficulte={difficulte} setDifficulte={setDifficulte} uploadedDocs={uploadedDocs} uploading={uploading} onGenerate={handleAIGenerate} onDropDocs={onDropDocs} onDeleteDoc={handleDeleteDoc} sciMode={sciMode} onToggleSciMode={() => setSciMode(s => !s)} />

      <QcmPreviewDialog quizId={quizId} quizTitle={titre} open={previewOpen} onClose={() => setPreviewOpen(false)} onNoStudents={() => setNoStudentsWarning(true)} />

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis]}>
        <SortableContext items={questions.map(q => q._id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-4">
            {questions.map((q, qIdx) => (
              <QuestionCard key={q._id} q={q} qIdx={qIdx} sortableId={q._id}
                onUpdateQuestion={updateQuestion} onUpdateOption={updateOption} onAddOption={addOption} onRemoveOption={removeOption}
                onRemoveQuestion={removeQuestion} onToggleCorrect={toggleCorrectAnswer} onToggleType={toggleQuestionType} canRemove={questions.length > 1}
                notionTags={notionTags} tagsTree={tagsTree} sciMode={sciMode} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <Button data-testid="add-question-btn" onClick={() => setQuestions(p => [...p, emptyQuestion()])} variant="secondary" className="w-full border border-dashed border-border hover:border-primary/50">
        <Plus className="w-4 h-4 mr-2" /> Ajouter une question
      </Button>

      <UnsavedChangesDialog open={unsavedPrompt} onOpenChange={setUnsavedPrompt} saving={saving}
        onPreviewAnyway={() => { setUnsavedPrompt(false); setPreviewOpen(true); }} onSaveAndPreview={handleSaveThenPreview} />
      <LeavePageDialog open={!!pendingHref} onCancel={cancelLeave} onDiscard={confirmLeave} onSaveAndLeave={handleSaveThenLeave} saving={saving} />
      <NoStudentsDialog open={noStudentsWarning} onOpenChange={setNoStudentsWarning} classe={classe} />
    </div>
  );
}
